const earthRadiusMeters = 6_371_000;

export const nearbyFavouriteLimit = 5;
export const nearbyFavouriteRadiusMeters = 500;

const toRadians = (degrees) => (degrees * Math.PI) / 180;

export function getStopDistanceMeters({
  latitude,
  longitude,
  stopLatitude,
  stopLongitude,
}) {
  if (
    ![latitude, longitude, stopLatitude, stopLongitude].every(Number.isFinite)
  ) {
    return null;
  }

  const latitudeDelta = toRadians(stopLatitude - latitude);
  const longitudeDelta = toRadians(stopLongitude - longitude);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(toRadians(latitude)) *
      Math.cos(toRadians(stopLatitude)) *
      Math.sin(longitudeDelta / 2) ** 2;

  return (
    2 *
    earthRadiusMeters *
    Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
  );
}

export function formatStopDistance(distanceMeters) {
  if (!Number.isFinite(distanceMeters)) return null;

  return distanceMeters < 1_000
    ? `${Math.round(distanceMeters)}m`
    : `${(distanceMeters / 1_000).toFixed(1)}km`;
}

export function compareStopsByDistance({ a, b, latitude, longitude }) {
  const aDistance = getStopDistanceMeters({
    latitude,
    longitude,
    stopLatitude: a?.stop_lat,
    stopLongitude: a?.stop_lon,
  });
  const bDistance = getStopDistanceMeters({
    latitude,
    longitude,
    stopLatitude: b?.stop_lat,
    stopLongitude: b?.stop_lon,
  });

  if (Number.isFinite(aDistance) && Number.isFinite(bDistance)) {
    return aDistance - bDistance;
  }
  if (Number.isFinite(aDistance)) return -1;
  if (Number.isFinite(bDistance)) return 1;
  return 0;
}

export function orderStopsWithNearbyFirst({
  stops,
  latitude,
  longitude,
  limit = nearbyFavouriteLimit,
  radiusMeters = nearbyFavouriteRadiusMeters,
}) {
  const measuredStops = (Array.isArray(stops) ? stops : []).map((stop) => ({
    ...stop,
    distanceMeters: getStopDistanceMeters({
      latitude,
      longitude,
      stopLatitude: stop.stop_lat,
      stopLongitude: stop.stop_lon,
    }),
  }));
  const safeLimit = Math.max(0, Math.floor(Number(limit) || 0));
  const safeRadius = Number(radiusMeters);

  if (!safeLimit || !Number.isFinite(safeRadius) || safeRadius <= 0) {
    return measuredStops;
  }

  const nearbyStops = measuredStops
    .filter(
      (stop) =>
        Number.isFinite(stop.distanceMeters) &&
        stop.distanceMeters < safeRadius,
    )
    .sort((a, b) => a.distanceMeters - b.distanceMeters)
    .slice(0, safeLimit);
  const nearbyStopSet = new Set(nearbyStops);

  return [
    ...nearbyStops,
    ...measuredStops.filter((stop) => !nearbyStopSet.has(stop)),
  ];
}
